import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSimplifiedAuth } from '@/contexts/SimplifiedAuthContext';
import { toast } from 'sonner';
import { useTranslation } from './useTranslation';

/**
 * Hook for switching between Nanny and Mother roles
 * Handles secure backend role switch, auth refresh, and navigation
 */
export const useNannyMotherRoleSwitch = () => {
  const [isSwitching, setIsSwitching] = useState(false);
  const { refetchData } = useSimplifiedAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('workspace');

  const performRoleSwitch = async (targetRole: 'nanny' | 'mother') => {
    setIsSwitching(true);
    
    try {
      console.log(`🔄 Initiating role switch to: ${targetRole}`);

      // Call edge function to switch roles securely
      const { data, error } = await supabase.functions.invoke('switch-nanny-platform-role', {
        body: { targetUserType: targetRole }
      });

      if (error) {
        console.error('❌ Role switch error:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error('Role switch failed');
      }

      console.log('✅ Role switched successfully:', data);

      // Clear auth cache to force fresh data
      localStorage.removeItem('auth_roles_cache');
      
      // Update workspace mode
      localStorage.setItem('workspace_mode', targetRole);

      // Refetch auth data to get updated roles
      await refetchData();

      // Show success message
      toast.success(
        t('role_switch_success', `Successfully switched to ${targetRole === 'nanny' ? 'Nanny' : 'Parent/Mother'} role`)
      );

      // Navigate to appropriate dashboard
      const targetRoute = targetRole === 'nanny' 
        ? '/workspace/nanny/dashboard' 
        : '/workspace/mother/dashboard';
      
      navigate(targetRoute);

    } catch (error) {
      console.error('❌ Failed to switch role:', error);
      toast.error(
        t('role_switch_error', "We couldn't switch your role. Please try again.")
      );
      throw error;
    } finally {
      setIsSwitching(false);
    }
  };

  const switchToNanny = () => performRoleSwitch('nanny');
  const switchToMother = () => performRoleSwitch('mother');

  return {
    isSwitching,
    switchToNanny,
    switchToMother,
    performRoleSwitch
  };
};
